import React, { useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { useResizeObserver } from '@hotjar/webapp/hooks/useResizeObserver';
import { useRealTimeStore } from '@hotjar/webapp/features/Playbacks/store/realTimeStore';
import { useRecordingHighlights } from '@hotjar/webapp/features/Highlights/Recordings';
import { round } from '@hotjar/webapp/features/Playbacks/Timeline/Section/SectionList';
import { MiniTimeline } from './MiniTimeline';

const computeRelativeProgress = (xActiveProgress: number, startFrom: number, endAt: number) => {
  if (xActiveProgress < startFrom) {
    return 0;
  }

  const xStartPosition = startFrom;
  const xEndPosition = startFrom + endAt;

  return ((xActiveProgress - xStartPosition) / (xEndPosition - xStartPosition)) * 100;
};

interface Props {
  duration: number;
  activeItemId: number;
  siteId: number;
  recordingId: number;
}

const TimelineWrapper = styled.div`
  width: 100%;
  position: absolute;
  bottom: 0;
  z-index: 0;
`;

export const TimelineListUnmemo = ({
  duration,
  activeItemId,
  siteId,
  recordingId,
}: Props): JSX.Element | null => {
  /*
    TimelineList is being shown only when sticker labels experiment and flag.highlights FF is active.
    If the Sticker Labels experiment is successful, data will be fetched from Playbacks instead.
  */
  const { data } = useRecordingHighlights(String(siteId), recordingId);
  const barRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver(barRef);
  const xActiveProgressInPx = useRealTimeStore(
    useCallback(
      (state) =>
        state.isDragging
          ? (state.draggedTime / duration) * width
          : (state.currentTimeWithLessPrecision / duration) * width,
      [duration, width],
    ),
  );
  const isHovering = useRealTimeStore((state) => state.isHovering);
  const hoveredPageIndex = useRealTimeStore((state) => state.hoveredPageIndex);
  const currentPageHovered = useRealTimeStore(
    useCallback(
      (state) => {
        const [firstSectionHovered, ...rest] = state.sectionInfoList.filter(
          (section) => section.pageIndex === hoveredPageIndex,
        );
        const totalDurationMs = rest.reduce((acc, curr) => {
          return curr.durationInMs + acc;
        }, firstSectionHovered.durationInMs);

        const res = {
          startTimeInMs: firstSectionHovered.startTimeInMs,
          endTimeInMs: firstSectionHovered.startTimeInMs + totalDurationMs,
          durationInMs: totalDurationMs,
        };
        return res;
      },
      [hoveredPageIndex],
    ),
  );

  const timelinesData = data?.items.map(({ id, metadata }) => ({
    id: Number(id),
    highlightStartTime: metadata.clip.start,
    highlightEndTime: metadata.clip.end,
    highlightDuration: metadata.clip.duration,
  }));

  return (
    <TimelineWrapper ref={barRef}>
      {timelinesData?.map(({ id, highlightStartTime, highlightEndTime, highlightDuration }) => {
        const xPositionInPx = round((highlightStartTime * width) / duration);
        const xWidthInPx = round((highlightDuration * width) / duration);
        return (
          <MiniTimeline
            isHovered={id === activeItemId}
            isRespectivePageHovered={
              isHovering &&
              // Full in range
              ((highlightStartTime >= currentPageHovered.startTimeInMs &&
                highlightStartTime <= currentPageHovered.endTimeInMs) ||
                // Partially in range
                (highlightStartTime <= currentPageHovered.startTimeInMs &&
                  (highlightEndTime >= currentPageHovered.endTimeInMs ||
                    highlightEndTime >= currentPageHovered.startTimeInMs)))
            }
            key={id}
            xPositionInPx={xPositionInPx}
            xWidthInPx={xWidthInPx}
            xProgressInPercent={computeRelativeProgress(
              xActiveProgressInPx,
              xPositionInPx,
              xWidthInPx,
            )}
          />
        );
      })}
    </TimelineWrapper>
  );
};

export const TimelineList = React.memo(TimelineListUnmemo);
